/**
 * @fileoverview ClubOS User Access Management page
 * Full CRUD user management with role-based visibility.
 * TODO: Replace MOCK_USERS with API calls: userService.getUsers(), userService.createUser(), etc.
 */

import { useState, useMemo } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// ClubOS auth & data
import { useAuth } from "context/AuthContext";
import { PermissionGate } from "components/PermissionGate";
import { MOCK_USERS, ROLES_LIST, DEPARTMENTS } from "data/userData";
import { ROLE_META, ROLE_PERMISSIONS } from "constants/roles";
import { formatDateIN } from "utils/indiaLocale";
import FootballLoader from "components/FootballLoader";

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

/** Status badge colour mapping */
const STATUS_COLORS = { Active: "success", Inactive: "error" };

/** Empty form state */
const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  department: "",
  role: "",
  status: "Active",
  sendInvite: true,
};

/** Validation rules */
function validateForm(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Full name is required";
  if (!form.email.trim()) errors.email = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Invalid email address";
  if (!form.department) errors.department = "Department is required";
  if (!form.role) errors.role = "Role is required";
  return errors;
}

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function UsersPage() {
  // TODO: Replace with API call
  // useEffect(() => { userService.getUsers().then(setUsers); }, []);
  const [users, setUsers] = useState(MOCK_USERS);
  const [loading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [deptFilter, setDeptFilter] = useState("All");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const { hasPermission } = useAuth();
  const canEdit = hasPermission("users.edit");
  const canAdmin = hasPermission("users.admin");

  // ── Filtered users ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return users.filter((u) => {
      const matchSearch =
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchRole = roleFilter === "All" || u.role === roleFilter;
      const matchStatus = statusFilter === "All" || u.status === statusFilter;
      const matchDept = deptFilter === "All" || u.department === deptFilter;
      return matchSearch && matchRole && matchStatus && matchDept;
    });
  }, [users, search, roleFilter, statusFilter, deptFilter]);

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.status === "Active").length;
  const rolesCount = new Set(users.map((u) => u.role)).size;
  const inactiveUsers = users.filter((u) => u.status === "Inactive").length;

  // ── Dialog handlers ───────────────────────────────────────────────────────
  const openAddDialog = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      department: user.department,
      role: user.role,
      status: user.status,
      sendInvite: false,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field on change
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSave = () => {
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (editingUser) {
      // Update existing user
      // TODO: await userService.updateUser(editingUser.id, form)
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id ? { ...u, ...form } : u
        )
      );
    } else {
      // Create new user
      // TODO: await userService.createUser(form)
      const newUser = {
        ...form,
        id: Math.max(...users.map((u) => u.id)) + 1,
        lastLogin: null,
        joinDate: new Date().toISOString().split("T")[0],
      };
      setUsers((prev) => [...prev, newUser]);
    }

    setDialogOpen(false);
  };

  const handleDeactivate = () => {
    if (!deactivateTarget) return;
    // TODO: await userService.deactivateUser(deactivateTarget.id)
    setUsers((prev) =>
      prev.map((u) =>
        u.id === deactivateTarget.id ? { ...u, status: "Inactive" } : u
      )
    );
    setDeactivateTarget(null);
  };

  // ── Role info panel ───────────────────────────────────────────────────────
  const selectedRoleMeta = ROLE_META[form.role];
  const selectedRolePermCount = form.role ? (ROLE_PERMISSIONS[form.role] || []).length : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              User Access Management
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Manage platform users and role-based permissions — FC Metropolitan
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="users.admin">
            <SoftButton variant="gradient" color="info" onClick={openAddDialog}>
              <Icon sx={{ mr: 0.5 }}>person_add</Icon>
              Add User
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Users" }}
                count={String(totalUsers)}
                percentage={{ color: "info", text: "platform accounts" }}
                icon={{ color: "info", component: "group" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Active Users" }}
                count={String(activeUsers)}
                percentage={{ color: "success", text: `${totalUsers - activeUsers} inactive` }}
                icon={{ color: "success", component: "check_circle" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Roles Defined" }}
                count={String(rolesCount)}
                percentage={{ color: "warning", text: "across departments" }}
                icon={{ color: "warning", component: "badge" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Inactive Accounts" }}
                count={String(inactiveUsers)}
                percentage={{ color: "error", text: "pending review" }}
                icon={{ color: "error", component: "person_off" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── User Table ──────────────────────────────────────────────────── */}
        <Card>
          <SoftBox p={3}>
            {/* Filters */}
            <Grid container spacing={2} mb={3} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <Icon sx={{ mr: 1, color: "#aaa", fontSize: "1.1rem" }}>search</Icon>,
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
                    <MenuItem value="All">All Roles</MenuItem>
                    {ROLES_LIST.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select value={deptFilter} label="Department" onChange={(e) => setDeptFilter(e.target.value)}>
                    <MenuItem value="All">All Depts</MenuItem>
                    {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <SoftTypography variant="caption" color="text">
                  {filtered.length} of {totalUsers} users
                </SoftTypography>
              </Grid>
            </Grid>

            {/* Table */}
            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox
                component="table"
                sx={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}
              >
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["User", "Email", "Role", "Department", "Status", "Last Login", "Actions"].map((h) => (
                      <SoftBox
                        key={h}
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 1.5,
                          color: "text.secondary",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          borderBottom: "2px solid #e9ecef",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {loading ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={7}><FootballLoader label="Loading users…" /></SoftBox>
                    </SoftBox>
                  ) : filtered.length === 0 ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={7} sx={{ textAlign: "center", p: 4 }}>
                        <Icon sx={{ fontSize: "2rem", color: "#ccc", display: "block", mx: "auto", mb: 1 }}>
                          search_off
                        </Icon>
                        <SoftTypography variant="caption" color="text">
                          No users found matching your filters.
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ) : (
                    filtered.map((user, i) => {
                      const roleMeta = ROLE_META[user.role] || {};
                      return (
                        <SoftBox
                          key={user.id}
                          component="tr"
                          sx={{
                            "&:hover": { backgroundColor: "#f8f9fa" },
                            backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                          }}
                        >
                          {/* Avatar + Name */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftBox display="flex" alignItems="center" gap={1.5}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  bgcolor: "#17c1e8",
                                }}
                              >
                                {user.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                              </Avatar>
                              <SoftTypography variant="button" fontWeight="medium">
                                {user.name}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>

                          {/* Email */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography variant="caption" color="text">{user.email}</SoftTypography>
                          </SoftBox>

                          {/* Role badge */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <Tooltip title={roleMeta.description || ""} placement="top">
                              <span>
                                <SoftBadge
                                  badgeContent={user.role}
                                  color={roleMeta.color || "info"}
                                  size="xs"
                                  container
                                />
                              </span>
                            </Tooltip>
                          </SoftBox>

                          {/* Department */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography variant="caption" color="text">{user.department}</SoftTypography>
                          </SoftBox>

                          {/* Status */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <Chip
                              label={user.status}
                              size="small"
                              color={user.status === "Active" ? "success" : "default"}
                              sx={{ fontSize: "0.65rem", height: 20 }}
                            />
                          </SoftBox>

                          {/* Last login */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftTypography variant="caption" color="text">
                              {user.lastLogin ? formatDateIN(user.lastLogin) : "Never"}
                            </SoftTypography>
                          </SoftBox>

                          {/* Actions */}
                          <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                            <SoftBox display="flex" gap={0.5}>
                              <PermissionGate permission="users.edit">
                                <Tooltip title="Edit user">
                                  <IconButton size="small" onClick={() => openEditDialog(user)}>
                                    <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                                  </IconButton>
                                </Tooltip>
                              </PermissionGate>
                              <PermissionGate permission="users.admin">
                                {user.status === "Active" && (
                                  <Tooltip title="Deactivate user">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setDeactivateTarget(user)}
                                    >
                                      <Icon sx={{ fontSize: "1rem" }}>person_off</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </PermissionGate>
                            </SoftBox>
                          </SoftBox>
                        </SoftBox>
                      );
                    })
                  )}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>

      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />

      {/* ── Add / Edit User Dialog ───────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {editingUser ? "Edit User" : "Add New User"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            {/* Full Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={form.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                required
                type="email"
                value={form.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone (Indian format)"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Department */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.department}>
                <InputLabel shrink>Department</InputLabel>
                <Select
                  value={form.department}
                  label="Department"
                  onChange={(e) => handleFormChange("department", e.target.value)}
                >
                  {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
                {formErrors.department && <FormHelperText>{formErrors.department}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.role}>
                <InputLabel shrink>Role</InputLabel>
                <Select
                  value={form.role}
                  label="Role"
                  onChange={(e) => handleFormChange("role", e.target.value)}
                >
                  {ROLES_LIST.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Role info panel — shown when a role is selected */}
            {form.role && selectedRoleMeta && (
              <Grid item xs={12}>
                <SoftBox
                  p={1.5}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, border: "1px solid #e9ecef" }}
                >
                  <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Icon sx={{ fontSize: "1rem", color: "#17c1e8" }}>{selectedRoleMeta.icon}</Icon>
                    <SoftTypography variant="caption" fontWeight="bold">{form.role}</SoftTypography>
                    <SoftBadge
                      badgeContent={`${selectedRolePermCount} permissions`}
                      color={selectedRoleMeta.color || "info"}
                      size="xs"
                      container
                    />
                  </SoftBox>
                  <SoftTypography variant="caption" color="text">
                    {selectedRoleMeta.description}
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={(e) => handleFormChange("status", e.target.value)}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Send invite toggle — only on new user */}
            {!editingUser && (
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.sendInvite}
                      onChange={(e) => handleFormChange("sendInvite", e.target.checked)}
                      color="info"
                    />
                  }
                  label={
                    <SoftTypography variant="caption">
                      Send invite email
                    </SoftTypography>
                  }
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave}>
            {editingUser ? "Save Changes" : "Create User"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Deactivate Confirmation Dialog ──────────────────────────────────── */}
      <Dialog
        open={Boolean(deactivateTarget)}
        onClose={() => setDeactivateTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "error.main" }}>
          Deactivate User
        </DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Are you sure you want to deactivate{" "}
            <strong>{deactivateTarget?.name}</strong>? They will no longer be able to log in to
            ClubOS. This action can be reversed by editing the user.
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDeactivateTarget(null)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleDeactivate}>
            Deactivate
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default UsersPage;
